import { Button, Divider, Dropdown, message, Modal, Space, Table, Tooltip, Typography } from 'antd';
import { useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY, MAIN_TOOLBAR_DEDUCT_SELECTORS } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { getSolutionsListApi, getUserFindAllApi, deleteSolutionByIdsApi, updateStatusApi } from '@services/Eha';
import './SolutionsList.less';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import config from '@config';
import PageHeader from '@components/PageHeader/PageHeader';
import SolutionsFilterPanel from './SolutionsFilterPanel';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';

const { Text } = Typography;

export default function SolutionsList() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const memCard = params.get('memCard');
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });
    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 获取列表数据
    const [data, setData] = useState([]);
    const [userData, setUserData] = useState([]);
    const [queryObj, setQueryObj] = useState({});
    // 分页设置
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    // 分页
    const onChangePage = (page, pageSize) => {
        setPage(page);
        setPageSize(pageSize);
    };

    const onFinish = res => {
        setPage(1);
        setQueryObj(res);
    };
    const onReset = () => {
        setPage(1);
        setQueryObj({});
    };

    const onDelete = row => {
        Modal.confirm({
            title: '提示',
            content: '解决方案删除后用户端将不再展示，确定要删除该解决方案吗？',
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                deleteSolutionByIdsApi([row.id]).then(res => {
                    if (res.status_code === 200) {
                        message.success(res.message);
                        getData();
                    }
                });
            },
        });
    };
    const onDownShelves = (status, row) => {
        if (status === 1) {
            updateStatusApi({ id: row.id, status: status }).then(res => {
                if (res.status_code === 200) {
                    message.success(res.message);
                    getData();
                }
            });
        } else {
            Modal.confirm({
                title: '提示',
                content: '解决方案下架后用户端将不再展示，确定要下架该解决方案吗？',
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    updateStatusApi({ id: row.id, status: status }).then(res => {
                        if (res.status_code === 200) {
                            message.success(res.message);
                            getData();
                        }
                    });
                },
            });
        }
    };

    const getDetail = row => {
        navigate(`/healthaccount/eha/solutions/edit/${row.id}`);
    };

    // 生成壁报
    const generatePoster = row => {
        navigate(`/healthaccount/eha/solutions/placard/${row.id}`);
    };

    const previewDetail = row => {
        window.open(`${config.SOLUTION_DOMAIN}/solutions/${row.id}?preview=1`);
    };

    const getData = useCallback(() => {
        const queryParams = {
            ...queryObj,
            page,
            limit: pageSize,
        };
        if (memCard) {
            queryParams.memCard = memCard;
        }
        if (queryObj.createdAt != null) queryParams.createAtBegin = dayjs(queryObj.createdAt[0]).format('YYYY-MM-DD HH:mm:ss');
        if (queryObj.createdAt != null) queryParams.createAtEnd = dayjs(queryObj.createdAt[1]).format('YYYY-MM-DD HH:mm:ss');
        if (queryObj.reviewedAt != null) queryParams.reviewedAtBegin = dayjs(queryObj.reviewedAt[0]).format('YYYY-MM-DD HH:mm:ss');
        if (queryObj.reviewedAt != null) queryParams.reviewedAtEnd = dayjs(queryObj.reviewedAt[1]).format('YYYY-MM-DD HH:mm:ss');
        if (queryObj.reviewStatus === -1) {
            delete queryParams.reviewStatus;
        }
        delete queryParams.createdAt;
        delete queryParams.reviewedAt;
        getSolutionsListApi(queryParams).then(res => {
            if (res && res.status_code === 200) {
                setData(res.data.list);
                setTotal(res.data.totalCount);
            }
        });
    }, [memCard, page, pageSize, queryObj]);

    useEffect(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        getUserFindAllApi({}).then(res => {
            if (res && res.status_code === 200) {
                setUserData(res.data);
            }
        });
    }, []);

    const columns = [
        { title: '解决方案名称', dataIndex: 'title', width: 300 },
        { title: '发布者昵称', dataIndex: 'nickName', width: 150 },
        { title: '发布者手机', dataIndex: 'mobile', width: 100 },
        { title: '提交时间', dataIndex: 'createdAt', width: 160 },
        {
            title: '审核状态',
            dataIndex: 'reviewStatus',
            width: 100,
            render: (reviewStatus, row) => (
                <>
                    {reviewStatus === 0 && <span> 待审核 </span>}
                    {reviewStatus === 1 && <Text type="success"> 审核通过 </Text>}
                    {reviewStatus === 2 && (
                        <Text type="warning">
                            {' '}
                            审核驳回{' '}
                            <Tooltip placement="left" color="#ffffff" title={<span className="ToolRejectReason">{row.rejectReason}</span>}>
                                <i className="bi bi-chat-dots"></i>
                            </Tooltip>
                        </Text>
                    )}
                </>
            ),
        },
        {
            title: '累计 PV/UV',
            dataIndex: 'pv',
            width: 100,
            render: (_, row) => (
                <>
                    {row.pv} / {row.uv}
                </>
            ),
        },
        {
            title: '审核时间',
            dataIndex: 'reviewedAt',
            width: 160,
            render: (_, row) => (
                <>
                    {row.reviewedName && <p className="reviewedNameClass"> {row.reviewedName} </p>}
                    {row.reviewedAt && <p className="reviewedAtClass"> {row.reviewedAt} </p>}
                </>
            ),
        },
        {
            title: '发布状态',
            dataIndex: 'status',
            width: 100,
            render: status => (
                <>
                    {status === 0 && <span> 待发布 </span>}
                    {status === 1 && <Text type="success"> 已发布 </Text>}
                    {status === 2 && <Text type="warning"> 已下架 </Text>}
                </>
            ),
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 260,
            fixed: 'right',
            render: (_, row) => {
                const items = [
                    {
                        label: '删除',
                        key: 'delete',
                        onClick: () => onDelete(row),
                        danger: true,
                    },
                ];

                return (
                    <span className="operation-btns">
                        <Button size="small" type="link" onClick={() => generatePoster(row)}>
                            生成壁报
                        </Button>
                        <Button size="small" type="link" onClick={() => previewDetail(row)}>
                            预览
                        </Button>
                        <Button size="small" type="link" onClick={() => getDetail(row)}>
                            审核
                        </Button>
                        {row.status === 1 && (
                            <Button size="small" type="link" onClick={() => onDownShelves(2, row)}>
                                下架
                            </Button>
                        )}
                        {row.status === 2 && (
                            <Button size="small" type="link" onClick={() => onDownShelves(1, row)}>
                                上架
                            </Button>
                        )}
                        <Divider orientation="vertical" />
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots" aria-hidden="true" />
                            </Button>
                        </Dropdown>
                    </span>
                );
            },
        },
    ];

    return (
        <div className="page-container list-page-container eha-solutions-list-page">
            <PageHeader title="解决方案管理" breadcrumb={[{ label: '健康号', path: '/healthaccount' }, { label: '解决方案管理' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <SolutionsFilterPanel userData={userData} onFinish={onFinish} onReset={onReset} />
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container eha-solutions-table-region">
                <div className="main-toolbar eha-solutions-toolbar">
                    <div className="main-tools">
                        <span className="table-title">解决方案列表</span>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={getData} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap eha-solutions-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="id"
                        columns={columns}
                        dataSource={data}
                        bordered
                        sticky
                        size="medium"
                        pagination={{
                            total: total,
                            showTotal: () => `共${total}条`,
                            current: page,
                            pageSize: pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onChange: onChangePage,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                        }}
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                    />
                </div>
            </div>
        </div>
    );
}
